import { useEffect, useState } from 'react'

import axios from 'axios'

import queryClient from 'Utils/queryClient'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { normalizeDesigner } from './useDesignerOptions'

// Contract: GET ?term=<fragment>, public, bare JSON array of up to 10 rows in the same snake_case
// shape as /api/internal/designers (username, model_count, rating_percent, top_designer, following),
// best matches first.
export const DESIGNER_LOOKUP_ENDPOINT = '/api/internal/designers/search'
export const DESIGNER_LOOKUP_QUERY_KEY = ['searchPage', 'designerLookup']
export const DESIGNER_LOOKUP_MIN_CHARS = 2
export const DESIGNER_LOOKUP_DEBOUNCE_MS = 300
const STALE_TIME = 5 * 60 * 1000

const IDLE = { designers: EMPTY_ARRAY, isLoading: false }

export const normalizeTerm = query => query.trim().toLowerCase()

export const fetchDesignerLookup = async (term) => {
  const { data } = await axios.get(DESIGNER_LOOKUP_ENDPOINT, { params: { term } })
  return (Array.isArray(data) ? data : [])
    .filter(designer => designer?.username)
    .map(normalizeDesigner)
}

/**
 * Looks up designers across the whole catalog for the typed query, debounced, once it has enough
 * characters. Results are keyed by the normalized term so a repeat of a recent query is served from
 * the shared react-query cache (SearchPage is not wrapped in a QueryClientProvider, hence the
 * imperative fetchQuery, as in useDesignerOptions).
 *
 * @param {string} query - Raw search box value
 * @returns {{ designers: Array, isLoading: boolean }}
 */
export default function useDesignerLookup(query) {
  const term = normalizeTerm(query)
  const isActive = term.length >= DESIGNER_LOOKUP_MIN_CHARS
  const [resolved, setResolved] = useState({ term: '', designers: EMPTY_ARRAY })

  useEffect(() => {
    if (!isActive) {
      return undefined
    }

    let isCurrent = true
    const timer = setTimeout(() => {
      queryClient
        .fetchQuery({
          queryKey: [...DESIGNER_LOOKUP_QUERY_KEY, term],
          queryFn: () => fetchDesignerLookup(term),
          staleTime: STALE_TIME,
          retry: false,
        })
        // A rejection is a transport failure; the local list is still there, so an empty remote
        // list is the right fallback.
        .catch(() => EMPTY_ARRAY)
        .then((designers) => {
          if (isCurrent) {
            setResolved({ term, designers })
          }
        })
    }, DESIGNER_LOOKUP_DEBOUNCE_MS)

    return () => {
      isCurrent = false
      clearTimeout(timer)
    }
  }, [isActive, term])

  if (!isActive) {
    return IDLE
  }

  if (resolved.term === term) {
    return { designers: resolved.designers, isLoading: false }
  }

  // Keep the previous hits that still match while the next request is in flight, so the list does
  // not empty out on every keystroke.
  return {
    designers: resolved.designers.filter(designer => designer.username.toLowerCase().includes(term)),
    isLoading: true,
  }
}
